import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  addAffirmPaymentToken,
  type BillingAddressPayload,
} from "@/networking/payments.api";
import { CartResponse } from "@/networking/carts.api";
import { convertPriceToPositiveInteger } from "@/lib/utils";
import { AFFIRM_LOCALE_CONFIG } from "@/lib/affirm-locale-config";

export interface AffirmCheckoutProps {
  cartToken: string;
  cartData: CartResponse | null;
  affirmPublicKey: string | undefined;
  onSuccess: () => Promise<void>;
  setPaymentError: (error: string | undefined) => void;
  shippingInfo: {
    first_name: string;
    last_name: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    email: string;
  };
  billingAddress?: BillingAddressPayload;
}

export const useAffirmCheckout = () => {
  const [affirmModalLoading, setAffirmModalLoading] = useState(false);

  const addAffirmPaymentTokenMutation = useMutation({
    mutationFn: addAffirmPaymentToken,
    onError: () => {
      console.error("Affirm checkout error");
    },
  });

  const handleTokenizeAffirmCheckout = async ({
    cartToken,
    cartData,
    affirmPublicKey,
    onSuccess,
    setPaymentError,
    shippingInfo,
    billingAddress,
  }: AffirmCheckoutProps) => {
    try {
      setAffirmModalLoading(true);

      const cartCountry = cartData?.cart.country.iso;
      const config =
        AFFIRM_LOCALE_CONFIG[cartCountry as keyof typeof AFFIRM_LOCALE_CONFIG];

      if (!config) {
        setPaymentError(
          `Affirm is not available in ${
            cartData?.cart.country.name || "this country"
          }`,
        );
        setAffirmModalLoading(false);
        return;
      }

      if (!window.affirm?.checkout) {
        console.error("Affirm is not initialized");
        setPaymentError("Unable to open Affirm checkout. Please try again.");
        setAffirmModalLoading(false);
        return;
      }

      const checkoutUrl = cartData?.meta.checkout_url;
      const currencyCode = cartData?.cart.currency_code;
      const cartItems = cartData?.cart.items;

      if (!checkoutUrl || !currencyCode || !cartItems?.length) {
        const missingFields = [
          !checkoutUrl && "checkout_url",
          !currencyCode && "currency_code",
          !cartItems?.length && "cart_items",
        ].filter(Boolean);
        console.error(
          "Affirm checkout blocked - missing cart data:",
          missingFields.join(", "),
        );
        setPaymentError("Cart data is incomplete. Please try again.");
        setAffirmModalLoading(false);
        return;
      }

      window.affirm.checkout({
        merchant: {
          user_confirmation_url: checkoutUrl,
          user_cancel_url: checkoutUrl,
          public_api_key: affirmPublicKey,
          user_confirmation_url_action: "POST",
          name: cartData?.cart.company.name,
        },
        shipping: {
          name: {
            full: `${shippingInfo.first_name || ""} ${shippingInfo.last_name || ""}`.trim(),
          },
          address: {
            street1: shippingInfo.address1,
            street2: shippingInfo.address2,
            city: shippingInfo.city,
            region1_code: shippingInfo.state,
            postal_code: shippingInfo.postal_code,
            country: config.country_code,
          },
          email: shippingInfo.email,
        },
        billing: billingAddress
          ? {
              name: {
                full: billingAddress.name || "",
              },
              address: {
                street1: billingAddress.address1,
                street2: billingAddress.address2 ?? undefined,
                city: billingAddress.city,
                region1_code: billingAddress.state,
                postal_code: billingAddress.postal_code,
                country: billingAddress.country_code,
              },
              email: shippingInfo.email,
            }
          : {
              name: {
                full: `${shippingInfo.first_name || ""} ${shippingInfo.last_name || ""}`.trim(),
              },
              address: {
                street1: shippingInfo.address1,
                street2: shippingInfo.address2,
                city: shippingInfo.city,
                region1_code: shippingInfo.state,
                postal_code: shippingInfo.postal_code,
                country: config.country_code,
              },
              email: shippingInfo.email,
            },
        items: cartItems.map((item) => ({
          display_name:
            item.product_title ?? item.product?.title ?? item.title ?? "",
          sku: item.product?.sku ?? item.variant?.sku ?? `item-${item.id}`,
          unit_price: convertPriceToPositiveInteger(item.price),
          qty: item.quantity,
          item_url:
            cartData.meta.shop_url && item.product?.id
              ? `${cartData.meta.shop_url}/products/${item.product.id}`
              : undefined,
        })),
        mode: "modal",
        currency: currencyCode,
        shipping_amount: Math.round((cartData?.cart.shipping_total ?? 0) * 100),
        tax_amount: Math.round((cartData?.cart.tax_total ?? 0) * 100),
        total: Math.round((cartData?.cart.amount_total ?? 0) * 100),
      });

      window.affirm.checkout.open({
        onFail: function (error: { checkout_token: string; reason: string }) {
          console.error("Affirm checkout error:", error);
          setAffirmModalLoading(false);
          if (error.reason !== "canceled") {
            setPaymentError("Affirm checkout could not be processed");
          }
        },
        onSuccess: async function (checkout: {
          created: string;
          checkout_token: string;
        }) {
          try {
            await addAffirmPaymentTokenMutation.mutateAsync({
              cartToken,
              paymentMethod: {
                token: checkout.checkout_token,
              },
              billingAddress,
            });
            await onSuccess();
          } catch (error) {
            console.error("Error processing Affirm token:", error);
            setPaymentError("Failed to process Affirm payment");
          } finally {
            setAffirmModalLoading(false);
          }
        },
        onValidationError: function (checkout_validation_error: unknown) {
          console.error(
            "Affirm checkout validation error:",
            checkout_validation_error,
          );
          setAffirmModalLoading(false);
          setPaymentError(
            "Affirm could not validate your information. Please try again.",
          );
        },
      });
    } catch (error) {
      console.error("Affirm checkout error:", error);
      setAffirmModalLoading(false);
      throw new Error("Unable to process checkout");
    }
  };

  return {
    handleTokenizeAffirmCheckout,
    affirmModalLoading,
  };
};
